import qs from "querystring";
import { apiUrl } from "@/config";
import axios from "axios";
import { paginationFilterSortInitialObject } from "@/store/constants";
import { apiErrorHandler } from "@/utils/util";

/**
 * get(), post(), put(), patch(), del(): convenience functions for making API
 * requests with the correct method.
 */
export async function get(basePath, query, extra) {
  let path = basePath;
  if (query) {
    path = `${path}?${qs.stringify(query)}`;
  }

  return request("get", path, null, extra);
}

export async function post(path, data, extra) {
  return request("post", path, data, extra);
}

export async function put(path, data, extra) {
  return request("put", path, data, extra);
}

export async function patch(path, data, extra) {
  return request("patch", path, data, extra);
}

export async function del(path, data, extra) {
  return request("delete", path, data, extra);
}

export async function head(path, extra) {
  return request("head", path, null, extra);
}

export async function upload(path, data, extra) {
  return request("post", path, data, {
    multipart: true,
    ...extra
  });
}

/**
 * Base function for all API requests. Exported for testing.
 */

export async function request(method, path, data, extra = {}) {
  try {
    let headers = {
      Accept: "application/json",
      "Content-Type": "application/json"
    };

    const finalPath = path.charAt(0) === "/" ? path : `/${path}`;

    const options = {
      method: method.toUpperCase(),
      withCredentials: !extra.noCredentials,
      url: extra.rawUrl ? path : `${apiUrl}${finalPath}`,
      headers
    };

    if (extra.responseType) {
      options.responseType = extra.responseType;
    }

    if (extra.multipart) {
      const formData = new FormData();
      for (const key in data) {
        const val = data[key];

        if (val instanceof Blob || typeof val !== "object") {
          formData.append(key, val);
        } else {
          formData.append(key, JSON.stringify(val));
        }
      }
      headers[
        "Content-Type"
      ] = `multipart/form-data; boundary=${formData._boundary}`;

      data = formData;
    } else if (data instanceof Blob && extra.fileName) {
      headers["X-Inkit-Filename"] = encodeURIComponent(extra.fileName);
    } else if (data instanceof File) {
      headers["X-Inkit-Filename"] = encodeURIComponent(extra.name);
    } else if (data) {
      headers["Content-Type"] = "application/json";
    }

    if (extra.headers) {
      headers = {
        ...headers,
        ...extra.headers
      };
    }

    options.data = data;
    options.headers = headers;
    const response = await axios(options);

    if (response.status === 204) {
      return Promise.resolve("OK");
    }
    return response.data;
  } catch (e) {
    // handle removed items which need to be displayed
    if (e.response && e.response.status === 410) {
      return Promise.resolve(e.response);
    }

    const newErrorFormat = extra && extra.newErrorFormat;

    return Promise.reject(apiErrorHandler(e, newErrorFormat));
  }
}

/**
 * Preparing query string for request sending according to general formatting
 * @param page
 * @param sort
 * @param search
 * @param page_size
 * @param queryString
 */
export const prepareQueryString = ({
  page = paginationFilterSortInitialObject.pagination.current_page,
  sort = { key: "-updated_at", value: false },
  search = {
    type: "general",
    value: ""
  },
  page_size = 25,
  queryString = ""
}) => {
  const reqPage = page ? `&page=${page}` : "";
  const reqSort = sort ? `&sort=${sort.order ? `-${sort.key}` : sort.key}` : "";
  const reqSearch = search ? `&search=${
    typeof search === "string"
      ? search.replace(" ", "%2B")
      : search.value.replace(" ", "%2B")
  }` : "";
  const pageSize = `page_size=${page_size}`;

  return `${pageSize}${reqPage}${reqSort}${reqSearch}${queryString}`;
};
